import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  fetchFSMs,
  deleteFSM,
  setFilters,
  clearSuccessMessage,
} from './fsmSlice';
import { TableSkeleton } from '../../components/LoadingSpinner';
import SuccessSnackbar from '../../components/SuccessSnackbar';
import DWLFLoader from '../../components/DWLFLoader';

const FSMListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    fsms,
    fsmsLoading,
    fsmsError,
    filters,
    isDeleting,
    successMessage,
  } = useSelector((state) => state.fsm);

  // Local state
  const [localFilters, setLocalFilters] = useState(filters);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fsmToDelete, setFsmToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchFSMs(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      setTimeout(() => {
        dispatch(clearSuccessMessage());
        setSnackbarOpen(false);
      }, 4000);
    }
  }, [successMessage, dispatch]);

  const handleFilterChange = (field, value) => {
    setLocalFilters({ ...localFilters, [field]: value });
  };

  const handleApplyFilters = () => {
    const appliedFilters = { ...localFilters, offset: 0 };
    dispatch(setFilters(appliedFilters));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      limit: 50,
      offset: 0,
      status: '',
      tags: [],
      complexity: '',
    };
    setLocalFilters(clearedFilters);
    dispatch(setFilters(clearedFilters));
  };

  const handlePageChange = (event, newPage) => {
    const newOffset = newPage * filters.limit;
    dispatch(setFilters({ ...filters, offset: newOffset }));
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    dispatch(setFilters({ ...filters, limit: newLimit, offset: 0 }));
  };

  const handleCreateNew = () => {
    navigate('/state-machines/new');
  };

  const handleEdit = (fsmId) => {
    navigate(`/state-machines/${fsmId}/edit`);
  };

  const handleView = (fsmId) => {
    navigate(`/state-machines/${fsmId}/view`);
  };

  const handleDeleteClick = (fsm) => {
    setFsmToDelete(fsm);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (fsmToDelete) {
      dispatch(deleteFSM(fsmToDelete.fsmId));
      setDeleteDialogOpen(false);
      setFsmToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFsmToDelete(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
    case 'active': return 'success';
    case 'draft': return 'warning';
    case 'archived': return 'default';
    default: return 'default';
    }
  };

  if (fsmsLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          State Machines
        </Typography>
        <DWLFLoader size="large" />
      </Container>
    );
  }

  if (fsmsError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {fsmsError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          State Machines
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          sx={{ 
            backgroundColor: 'var(--dwlf-accent)',
            color: 'var(--dwlf-bg)',
            '&:hover': {
              backgroundColor: 'var(--dwlf-text)',
            }
          }}
        >
          New State Machine
        </Button>
      </Box>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '240px 240px 1fr 120px 120px' },
            gap: 2
          }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Complexity</InputLabel>
              <Select
                value={localFilters.complexity}
                label="Complexity"
                onChange={(e) => handleFilterChange('complexity', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="simple">Simple</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="complex">Complex</MenuItem>
              </Select>
            </FormControl>
            <Box />
            <Button variant="contained" onClick={handleApplyFilters}>Apply</Button>
            <Button variant="outlined" onClick={handleClearFilters}>Clear</Button>
          </Box>
        </Paper>
      </motion.div>

      {/* FSMs Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Nodes / Edges</TableCell>
                <TableCell>Last Modified</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fsms.map((fsm) => (
                <TableRow 
                  key={fsm.fsmId} 
                  hover
                  onClick={() => handleEdit(fsm.fsmId)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {fsm.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {fsm.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={fsm.status || 'draft'}
                      color={getStatusColor(fsm.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {fsm.stats?.totalNodes || fsm.visual?.nodes?.length || 0} nodes, {fsm.stats?.totalEdges || fsm.visual?.edges?.length || 0} edges
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {fsm.updatedAt ? formatDate(fsm.updatedAt) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => handleView(fsm.fsmId)}
                        title="View"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(fsm.fsmId)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(fsm)}
                        disabled={isDeleting}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {fsms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No state machines yet. Create your first one!
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={-1}
            page={Math.floor(filters.offset / filters.limit)}
            onPageChange={handlePageChange}
            rowsPerPage={filters.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete State Machine</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{fsmToDelete?.name}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <SuccessSnackbar
        open={snackbarOpen}
        message={successMessage}
        onClose={() => setSnackbarOpen(false)}
      />
    </Container>
  );
};

export default FSMListPage;
